// Track C.10 / Batches 7.2-7.8 — NotebookLM-style chat source panel.
//
// Backend ships a ``citations: [...]`` array on every assistant chat
// reply (see ``app/services/chat_citations.py:resolve_citations``).
// Each entry has shape:
//   { chunk_id, doc_id, status, filename?, page?, message? }
//   status ∈ "live" | "soft_deleted" | "hard_deleted"
//
// This component renders that array as a row of "source pills" under
// the assistant bubble:
//   * live      → filename + page badge + click-to-open the source panel
//   * soft_del  → grayed out + "trash" badge + tooltip with grace expiry
//   * hard_del  → strikethrough + "no longer available" tooltip
//
// Click a live pill → opens the SourcePanel modal which shows:
//   * the full filename
//   * page number
//   * the page image preview (when MinIO has a render — see
//     ``ingestion-figures/{agent_id}/{job_id}/page_NNNN.png`` from
//     Batch 4.1)
//   * the chunk text excerpt
//
// We don't actually fetch the page image inline in the chat bubble —
// just show the pill. The full panel opens on click. That keeps chat
// scrollback fast even on retrieval-heavy answers (10+ citations).

import React, { useState, useEffect } from "react";
import { COLORS } from "../theme/colors";
import { Badge } from "./ui";
import { request } from "../api/client";


export function ChatCitations({ citations, agentId }) {
  const [openCitation, setOpenCitation] = useState(null);

  if (!citations || citations.length === 0) return null;

  return (
    <>
      <div style={{
        marginTop: 8, paddingTop: 8, borderTop: `1px dashed ${COLORS.border}`,
        display: "flex", flexWrap: "wrap", gap: 6,
      }}>
        <span style={{ fontSize: 10, color: COLORS.textMuted, fontWeight: 600,
                       letterSpacing: "0.06em", textTransform: "uppercase",
                       alignSelf: "center", marginRight: 4 }}>
          Sources:
        </span>
        {citations.map((c, i) => {
          const isLive = c.status === "live";
          const isSoft = c.status === "soft_deleted";
          const isHard = c.status === "hard_deleted";
          const label = c.filename || c.doc_id || c.chunk_id;
          const pageStr = c.page != null ? ` p.${c.page}` : "";
          const tooltip = isHard
            ? (c.message || "Source no longer available")
            : isSoft
              ? (c.message || "Source has been deleted (in trash)")
              : `Open ${label}${pageStr}`;

          return (
            <button
              key={c.chunk_id || c.doc_id || i}
              onClick={() => isLive && setOpenCitation({ ...c, agentId })}
              disabled={!isLive}
              title={tooltip}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 4,
                padding: "3px 8px",
                fontSize: 11,
                fontWeight: 500,
                borderRadius: 12,
                border: `1px solid ${isHard ? "#ef4444" : isSoft ? "#f59e0b" : COLORS.accent}40`,
                background: isHard ? "#ef444415" : isSoft ? "#f59e0b15" : `${COLORS.accent}10`,
                color: isHard ? "#ef4444" : isSoft ? "#f59e0b" : COLORS.accent,
                cursor: isLive ? "pointer" : "not-allowed",
                opacity: isLive ? 1 : 0.7,
                textDecoration: isHard ? "line-through" : "none",
                fontFamily: "inherit",
              }}
            >
              <span style={{ fontSize: 10 }}>
                {isHard ? "\u2717" : isSoft ? "\uD83D\uDDD1\uFE0F" : "\uD83D\uDCC4"}
              </span>
              <span style={{ maxWidth: 180, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {label}
              </span>
              {c.page != null && (
                <span style={{ fontSize: 10, opacity: 0.8 }}>p.{c.page}</span>
              )}
            </button>
          );
        })}
      </div>

      {openCitation && (
        <SourcePanel
          citation={openCitation}
          onClose={() => setOpenCitation(null)}
        />
      )}
    </>
  );
}


// Source panel modal — shows the full citation context.
//
// Page image preview path: when the backend's chat_citations resolved
// a live row with a non-null ``page``, the corresponding MinIO key is
// ``ingestion-figures/{agentId}/{job_id}/page_{page:04d}.png``. The
// frontend has no direct MinIO access, so the API exposes
// ``GET /api/ingestion/documents/{doc_id}/page-image/{page}`` (signed
// URL or proxy) — see backend route added alongside this component.
//
// Falls back to "preview not available" when the doc was ingested
// before Batch 4.1's page-image upload (legacy corpus) or when the
// document type doesn't produce per-page images (HTML, MD).
function SourcePanel({ citation, onClose }) {
  const pageUrl = citation.doc_id && citation.page != null
    ? `/api/ingestion/documents/${citation.doc_id}/page-image/${citation.page}`
      // Scope to the citing agent: the same doc_id can exist under several
      // agents and only the one that ingested with page renders has them.
      + (citation.agentId ? `?agent_id=${encodeURIComponent(citation.agentId)}` : "")
    : null;

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)",
        display: "flex", alignItems: "center", justifyContent: "center",
        zIndex: 9999, padding: 20,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: COLORS.surface,
          borderRadius: 12,
          border: `1px solid ${COLORS.border}`,
          padding: 20,
          width: "min(720px, 96vw)",
          maxHeight: "92vh",
          display: "flex",
          flexDirection: "column",
          boxShadow: "0 20px 60px rgba(0,0,0,0.4)",
        }}
      >
        {/* Header */}
        <div style={{ display: "flex", alignItems: "center", marginBottom: 12 }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: COLORS.text }}>
              {citation.filename || citation.doc_id}
            </div>
            <div style={{ fontSize: 11, color: COLORS.textMuted, marginTop: 2 }}>
              {citation.page != null ? `Page ${citation.page} \u00B7 ` : ""}
              <span style={{ fontFamily: "monospace" }}>{citation.chunk_id}</span>
            </div>
          </div>
          <button
            onClick={onClose}
            style={{
              background: "transparent",
              border: `1px solid ${COLORS.border}`,
              borderRadius: 6,
              padding: "4px 10px",
              cursor: "pointer",
              color: COLORS.textMuted,
              fontSize: 12,
            }}
          >
            Close
          </button>
        </div>

        {/* Page-image preview (Batch 4.1's MinIO upload product) */}
        <div style={{ flex: 1, overflow: "auto", padding: 12, background: COLORS.background, borderRadius: 8, border: `1px solid ${COLORS.border}` }}>
          {pageUrl ? (
            <PageImageWithFallback url={pageUrl} alt={`Page ${citation.page} of ${citation.filename}`} />
          ) : (
            <div style={{ padding: 40, textAlign: "center", color: COLORS.textMuted, fontSize: 12 }}>
              {citation.message || "No page-image preview for this source."}
            </div>
          )}
        </div>

        {/* Chunk excerpt — when backend ships ``excerpt`` on the citation */}
        {citation.excerpt && (
          <div style={{ marginTop: 12, padding: 12, fontSize: 12, color: COLORS.text,
                        background: COLORS.background, borderRadius: 8,
                        border: `1px solid ${COLORS.border}`, fontStyle: "italic",
                        maxHeight: 160, overflowY: "auto" }}>
            "{citation.excerpt}"
          </div>
        )}
      </div>
    </div>
  );
}


// Loads the page image with a graceful "not available" fallback.
//
// The page-image endpoint is auth-gated (require_developer), so we CANNOT
// use a bare <img src> — a browser image request carries no Authorization
// header (the JWT lives in localStorage and is only attached by the api
// client), so the endpoint returned 401 for every source and the preview
// never rendered. Instead we fetch the bytes with the Bearer token and
// hand the <img> a blob: URL. The endpoint still 404s for documents with
// no page renders (legacy corpus / non-paginated formats) → fallback.
function PageImageWithFallback({ url, alt }) {
  const [blobUrl, setBlobUrl] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    let objectUrl = null;
    setError(false);
    setBlobUrl(null);

    (async () => {
      try {
        // ARC-013: route through the client `request` helper (auth header +
        // 401→refresh contract) instead of a hand-rolled fetch. rawResponse
        // returns the Response on success; non-2xx throws → fallback below.
        const res = await request(url, { rawResponse: true });
        const blob = await res.blob();
        if (cancelled) return;
        objectUrl = URL.createObjectURL(blob);
        setBlobUrl(objectUrl);
      } catch (e) {
        if (!cancelled) setError(true);
      }
    })();

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [url]);

  if (error) {
    return (
      <div style={{ padding: 40, textAlign: "center", color: COLORS.textMuted, fontSize: 12 }}>
        Page-image preview not available for this source. (Re-ingest the document to generate page renders.)
      </div>
    );
  }
  if (!blobUrl) {
    return (
      <div style={{ padding: 40, textAlign: "center", color: COLORS.textMuted, fontSize: 12 }}>
        Loading page preview…
      </div>
    );
  }
  return (
    <img
      src={blobUrl}
      alt={alt}
      onError={() => setError(true)}
      style={{
        width: "100%",
        height: "auto",
        display: "block",
        borderRadius: 4,
      }}
    />
  );
}


export default ChatCitations;
